import React from 'react';
import { Link } from 'react-router-dom';
import { MarketingLayout } from '../components/marketing/MarketingLayout';
import { Seo } from '../components/marketing/Seo';
import { MobileMockup } from '../components/marketing/Mockups';
import { PlanScene, type PlanScenePalette } from '../components/marketing/PlanScene';
import { useScrollReveal } from '../components/marketing/useScrollReveal';

// ── Data ──────────────────────────────────────────────────────────────────────

const workflow: Array<{
  step: string;
  title: string;
  description: string;
}> = [
  {
    step: '01',
    title: 'Rzut lub obiekt',
    description:
      'Dodajesz obiekt (budynek, lokal, infrastrukturę), importujesz rzut PDF lub obraz i tworzysz formularz pod swój typ inspekcji w wizualnym kreatorze.',
  },
  {
    step: '02',
    title: 'Markery i zdjęcia w terenie',
    description:
      'Inspektor wchodzi w teren, wskazuje punkt na rzucie, dodaje zdjęcia z GPS, wypełnia formularz i notuje uwagi — wszystko działa offline.',
  },
  {
    step: '03',
    title: 'Synchronizacja po powrocie zasięgu',
    description:
      'Zmiany zapisane offline trafiają do kolejki i synchronizują się automatycznie po powrocie zasięgu. Pełna widoczność statusu.',
  },
  {
    step: '04',
    title: 'Raport i eksport',
    description:
      'Generujesz raport PDF, eksport Excel lub paczkę ZIP. Klient otrzymuje dostęp do dokumentacji w dedykowanym portalu.',
  },
];

const modules = [
  {
    title: 'Kreator formularzy',
    description:
      'Tworzysz własne szablony inspekcji z sekcjami, logiką warunkową, polami zdjęciowymi, lokalizacją GPS i słownikami — wszystko samodzielnie, bez programisty.',
  },
  {
    title: 'Markery na rzutach i PDF',
    description:
      'Wskazujesz punkt na planie architektonicznym, PDF lub obrazie. Każdy marker ma zdjęcia, formularz, status, priorytet i historię zmian.',
  },
  {
    title: 'Aplikacja mobilna i praca offline',
    description:
      'Działa bez zasięgu — w piwnicy, na hali, na budowie. Dane zapisują się na urządzeniu i synchronizują po powrocie sieci. Aplikacje na iPhone’a i Androida wkrótce w App Store i Google Play.',
  },
  {
    title: 'Cała ekipa w cenie',
    description:
      'Stała opłata za organizację — płacisz za cały zespół, nie za każdego użytkownika. Dodajesz ekipę terenową, klientów i przeglądających bez dodatkowych opłat.',
  },
];

const featureGroups = [
  {
    title: 'Dokumentacja miejsca',
    items: [
      'markery na rzutach PDF, obrazach i mapach obiektów',
      'zdjęcia, adnotacje, komentarze i załączniki przy punkcie',
      'galeria zdjęć obiektu i historia zmian w audycie',
    ],
  },
  {
    title: 'Formularze terenowe',
    items: [
      'szablony formularzy z sekcjami i logiką warunkową',
      'zdjęcia per pole, lokalizacja GPS lub wskazanie na rzucie',
      'szkice offline i zatwierdzanie wypełnień',
    ],
  },
  {
    title: 'Praca zespołowa',
    items: [
      'role: administrator, użytkownik, przeglądający i klient zewnętrzny',
      'udostępnianie projektów lub wybranych obiektów klientowi',
      'powiadomienia, dziennik projektu i historia pobrań',
    ],
  },
  {
    title: 'Raportowanie',
    items: [
      'raporty PDF dla obiektu i projektu',
      'eksport Excel do dalszej analizy',
      'pełne paczki ZIP z dokumentacją i zdjęciami',
    ],
  },
];

const useCases: Array<{
  title: string;
  story: string;
  tags: string[];
}> = [
  {
    title: 'Przegląd okresowy budynku',
    story:
      'Inspektor wypełnia formularz przeglądu per kondygnację, oznacza punkty kontrolne na rzucie i generuje protokół PDF gotowy do przekazania zarządcy lub organom nadzoru budowlanego.',
    tags: ['obiekty i kondygnacje', 'formularze przeglądu', 'raport PDF'],
  },
  {
    title: 'Inwentaryzacja usterek na rzucie',
    story:
      'Ekipa wstawia markery przy każdej usterce na rzucie PDF, dodaje zdjęcia z GPS i opisem, ustawia status i priorytet. Raport z mapą markerów idzie do klienta bez sklejania z folderu zdjęć.',
    tags: ['markery na PDF', 'zdjęcia z GPS', 'statusy i priorytety'],
  },
  {
    title: 'Odbiór mieszkań — protokół odbioru',
    story:
      'Inwentaryzator odbiorczy oznacza wady na planie, wypełnia formularz z kategorią i szacowanym czasem naprawy. Protokół odbioru w PDF trafia do dewelopera i klienta.',
    tags: ['formularze odbioru', 'zdjęcia wad na rzucie', 'raport dla klienta'],
  },
  {
    title: 'Audyt terenowy offline',
    story:
      'Piwnica, hala, budowa bez zasięgu — Pinveo zapisuje dane lokalnie i synchronizuje po powrocie do sieci. Żaden wpis nie przepada, status synchronizacji widoczny na bieżąco.',
    tags: ['praca bez sieci', 'synchronizacja po powrocie zasięgu', 'aplikacja mobilna'],
  },
];

const metrics: [string, string][] = [
  ['Offline', 'tryb terenowy'],
  ['PDF·XLS·ZIP', 'eksporty'],
  ['Dostęp klienta', 'portal dokumentów'],
  ['Web + Mobile', 'desktop·tablet·tel'],
];

const trustItems = [
  {
    tag: 'RODO',
    title: 'Prywatność i RODO',
    body: 'Przetwarzanie danych zgodne z RODO; polityka prywatności i kontrola dostępu per rola.',
  },
  {
    tag: 'Export',
    title: 'Eksport w każdej chwili',
    body: 'Pełne paczki ZIP, raporty PDF i arkusze Excel — zabierasz swoje dane, kiedy chcesz.',
  },
  {
    tag: 'Open',
    title: 'Bez vendor lock-in',
    body: 'Otwarte formaty eksportu i pełna dokumentacja na wyjściu — żadnej pułapki dostawcy.',
  },
  {
    tag: 'Offline',
    title: 'Wpisy z terenu nie giną',
    body: 'Kolejka synchronizacji zapisuje każdy wpis z terenu lokalnie i wysyła go po powrocie zasięgu.',
  },
];

const forWhom = [
  {
    title: 'Firmy inspekcyjne / przeglądy techniczne',
    body: 'Obowiązkowe przeglądy okresowe, protokoły i raporty PDF gotowe dla zarządcy lub nadzoru.',
  },
  {
    title: 'Zarządcy nieruchomości / FM',
    body: 'Dokumentacja stanu, rejestr usterek, historia obiektu i raporty dla właściciela.',
  },
  {
    title: 'Deweloperzy i odbiory mieszkań',
    body: 'Protokoły odbioru, wady oznaczone na rzucie, zdjęcia z GPS i raport dla klienta.',
  },
  {
    title: 'BHP i audyty',
    body: 'Konfigurowalne checklisty i formularze, rejestr niezgodności i zalecenia pokontrolne.',
  },
  {
    title: 'Instalacje, geodezja, utrzymanie',
    body: 'Markery na rzutach i mapach, zdjęcia z GPS i pełna praca offline w terenie.',
  },
];

// ── Data: use-case cards dla sekcji Zastosowania ──────────────────────────────

const useCaseCards: Array<{ slug: string; title: string; label: string }> = [
  { slug: 'przeglady-okresowe-budynkow', title: 'Przeglądy okresowe budynków', label: 'inspekcje techniczne' },
  { slug: 'odbiory-mieszkan', title: 'Odbiory mieszkań od dewelopera', label: 'odbiory' },
  { slug: 'inwentaryzacja-usterek', title: 'Inwentaryzacja usterek (snagging)', label: 'budowa' },
  { slug: 'bhp-audyty', title: 'Audyty i kontrole BHP', label: 'bezpieczeństwo' },
  { slug: 'zarzadzanie-nieruchomosciami', title: 'Zarządzanie nieruchomościami', label: 'FM' },
  { slug: 'przeglady-instalacji-elektrycznej', title: 'Przeglądy instalacji elektrycznej', label: 'instalacje' },
  { slug: 'przeglady-placow-zabaw', title: 'Przeglądy placów zabaw', label: 'obiekty publiczne' },
  { slug: 'instalacje-geodezja', title: 'Dokumentacja instalacji i prac terenowych', label: 'instalacje · geodezja' },
];

// ── Data: blog cards dla sekcji Z bloga ───────────────────────────────────────

const blogCards: Array<{ slug: string; title: string; label: string }> = [
  { slug: 'przeglady-okresowe-budynku-terminy-kary', title: 'Przeglądy okresowe budynku — terminy i kary', label: 'prawo budowlane' },
  { slug: 'ksiazka-obiektu-budowlanego-co-zawiera', title: 'Książka obiektu budowlanego — co zawiera', label: 'dokumentacja' },
  { slug: 'najczestsze-usterki-odbior-mieszkania', title: 'Najczęstsze usterki przy odbiorze mieszkania', label: 'odbiory' },
  { slug: 'lista-kontrolna-bhp-na-budowie', title: 'Lista kontrolna BHP na budowie', label: 'BHP' },
];

const faqItems = [
  {
    q: 'Czy Pinveo to gotowy produkt?',
    a: 'Jesteśmy we wczesnym dostępie. Rdzeń — markery na rzutach, formularze, raporty i praca offline — jest używalny i testowany w terenie. Dołączając teraz masz realny wpływ na priorytety roadmapy.',
  },
  {
    q: 'Ile to kosztuje?',
    a: 'Stała opłata za organizację: płacisz za cały zespół, nie za liczbę użytkowników. W trakcie wczesnego dostępu testerzy korzystają bez opłat; plany Free/Basic/Pro uruchomimy później.',
  },
  {
    q: 'Co z moimi danymi?',
    a: 'Przetwarzanie zgodne z RODO, eksport w każdej chwili (ZIP/PDF/Excel) i brak vendor lock-in. Dane pozostają Twoje.',
  },
  {
    q: 'Czy działa offline?',
    a: 'Tak — to fundament aplikacji. Markery, zdjęcia i formularze zapisują się lokalnie i synchronizują automatycznie po powrocie zasięgu.',
  },
  {
    q: 'Skąd pobiorę aplikację?',
    a: 'Aplikacje na iPhone’a i Androida trafią do App Store i Google Play wkrótce. Uczestnicy wczesnego dostępu dostają je już teraz — po zapisaniu się wysyłamy zaproszenie z instrukcją instalacji.',
  },
  {
    q: 'Czy mogę udostępnić wyniki klientowi?',
    a: 'Tak. Rola klienta daje dostęp tylko do odczytu do wybranych projektów lub obiektów, wraz z portalem pobierania dokumentacji.',
  },
  {
    q: 'Dla kogo jest Pinveo?',
    a: 'Dla zespołów dokumentujących stan obiektów: firm inspekcyjnych, zarządców nieruchomości, deweloperów (odbiory), działów BHP/audytu oraz instalacji i geodezji.',
  },
  {
    q: 'Jak zacząć?',
    a: 'Poproś o wczesny dostęp i opisz swój przypadek użycia — pomożemy skonfigurować szablony formularzy i zakres pod Twój zespół.',
  },
];

// ── Inline SVG icons for the 4 filary (distinct, purpose-built) ────────────────

function IconFormBuilder() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: 24, height: 24 }}
    >
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <line x1="8" y1="8" x2="16" y2="8" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="16" x2="13" y2="16" />
      <path d="M14 15l1.5 1.5L18 14" stroke="#ff5a1f" strokeWidth="1.8" />
    </svg>
  );
}

function IconPinOnPlan() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: 24, height: 24 }}
    >
      <rect x="3" y="4" width="18" height="14" rx="1" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="4" x2="9" y2="18" />
      <circle cx="15" cy="13" r="2.4" fill="#ff5a1f" stroke="none" />
    </svg>
  );
}

function IconOfflineSync() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: 24, height: 24 }}
    >
      <path d="M6 16a4 4 0 0 1 .6-7.97A5.5 5.5 0 0 1 17.5 9 3.5 3.5 0 0 1 18 16H6z" />
      <path d="M9 19l2 2 4-4" stroke="#ff5a1f" strokeWidth="1.9" />
    </svg>
  );
}

function IconTeam() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: 24, height: 24 }}
    >
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M15 19a4.5 4.5 0 0 1 5.5-4.4" />
    </svg>
  );
}

const pillarIcons: Array<() => React.JSX.Element> = [IconFormBuilder, IconPinOnPlan, IconOfflineSync, IconTeam];

// Kolory sceny 3D w hero — paleta „papieru" landingu, nie tokeny aplikacji.
const LANDING_SCENE_PALETTE: PlanScenePalette = {
  plate: '#faf7f0',
  ink: '#14181f',
  inkSoft: '#8a8372',
  grid: '#e6dcc8',
};

// ── Blueprint Sheet Mockup (hero right column) ─────────────────────────────────

function BlueprintSheet() {
  return (
    <div className="bp-sheet bp-rise bp-d3" role="img" aria-label="Mockup rzutu z markerami inspekcji">
      <div className="bp-sheet-grid" aria-hidden="true" />
      <div className="bp-sheet-ticks" aria-hidden="true" />

      {/* Floor plan area */}
      <div
        style={{
          position: 'relative',
          height: 390,
          border: '1px dashed #cdbfa6',
        }}
        aria-hidden="true"
      >
        {/* Walls */}
        <div style={{ position: 'absolute', left: '6%', top: '8%', width: '60%', height: 2, background: '#14181f', opacity: 0.78 }} />
        <div style={{ position: 'absolute', left: '6%', top: '8%', width: 2, height: '80%', background: '#14181f', opacity: 0.78 }} />
        <div style={{ position: 'absolute', left: '6%', bottom: '12%', width: '88%', height: 2, background: '#14181f', opacity: 0.78 }} />
        <div style={{ position: 'absolute', right: '6%', top: '8%', width: 2, height: '80%', background: '#14181f', opacity: 0.78 }} />
        <div style={{ position: 'absolute', left: '40%', top: '8%', width: 2, height: '46%', background: '#14181f', opacity: 0.78 }} />
        <div style={{ position: 'absolute', left: '6%', top: '46%', width: '54%', height: 2, background: '#14181f', opacity: 0.78 }} />

        {/* Rooms */}
        <div style={{ position: 'absolute', left: '11%', top: '14%', width: '24%', height: '26%', border: '2px solid #c4b393', background: 'rgba(255,255,255,0.55)' }} />
        <div style={{ position: 'absolute', left: '46%', top: '14%', width: '42%', height: '30%', border: '2px solid #c4b393', background: 'rgba(255,255,255,0.55)' }} />
        <div style={{ position: 'absolute', left: '11%', top: '52%', width: '30%', height: '30%', border: '2px solid #c4b393', background: 'rgba(255,255,255,0.55)' }} />

        {/* Marker 18 — orange/hi */}
        <div
          style={{
            position: 'absolute', left: '20%', top: '22%',
            width: 34, height: 34, borderRadius: '50%',
            background: '#ff5a1f', display: 'grid', placeItems: 'center',
            color: '#fff', fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontWeight: 600, fontSize: 13, border: '2px solid #fff',
            boxShadow: '0 6px 16px rgba(20,24,31,0.28)', zIndex: 3,
          }}
          className="bp-pin bp-pin-1"
        >18</div>
        <div
          style={{
            position: 'absolute', left: '27%', top: '16%',
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 10.5,
            letterSpacing: '0.04em', color: '#3a414e',
            background: 'rgba(251,248,241,0.9)', padding: '2px 6px',
            border: '1px solid #cdbfa6', whiteSpace: 'nowrap', zIndex: 3,
          }}
        >M-18 · zawilgocenie ↑</div>

        {/* Marker 19 — amber */}
        <div
          style={{
            position: 'absolute', left: '62%', top: '26%',
            width: 34, height: 34, borderRadius: '50%',
            background: '#d9920a', display: 'grid', placeItems: 'center',
            color: '#fff', fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontWeight: 600, fontSize: 13, border: '2px solid #fff',
            boxShadow: '0 6px 16px rgba(20,24,31,0.28)', zIndex: 3,
          }}
          className="bp-pin bp-pin-2"
        >19</div>
        <div
          style={{
            position: 'absolute', right: '6%', top: '40%',
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 10.5,
            letterSpacing: '0.04em', color: '#3a414e',
            background: 'rgba(251,248,241,0.9)', padding: '2px 6px',
            border: '1px solid #cdbfa6', whiteSpace: 'nowrap', zIndex: 3,
          }}
        >M-19 · pęknięcie</div>

        {/* Marker 20 — ink */}
        <div
          style={{
            position: 'absolute', left: '23%', top: '64%',
            width: 34, height: 34, borderRadius: '50%',
            background: '#14181f', display: 'grid', placeItems: 'center',
            color: '#fff', fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontWeight: 600, fontSize: 13, border: '2px solid #fff',
            boxShadow: '0 6px 16px rgba(20,24,31,0.28)', zIndex: 3,
          }}
          className="bp-pin bp-pin-3"
        >20</div>
        <div
          style={{
            position: 'absolute', left: '31%', top: '56%',
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 10.5,
            letterSpacing: '0.04em', color: '#3a414e',
            background: 'rgba(251,248,241,0.9)', padding: '2px 6px',
            border: '1px solid #cdbfa6', whiteSpace: 'nowrap', zIndex: 3,
          }}
        >M-20 · instalacja</div>

        {/* Title block */}
        <div
          className="bp-title-block"
          style={{
            position: 'absolute', right: 0, bottom: 0,
            border: '1.5px solid #14181f', background: '#faf7f0',
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 11, zIndex: 4,
          }}
        >
          {[
            ['Projekt', 'Centrum Północ'],
            ['Arkusz', 'A-01'],
            ['Rew.', '03'],
          ].map(([label, val]) => (
            <div
              key={label}
              style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '5px 9px', borderBottom: '1px solid #cdbfa6',
              }}
            >
              <span style={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
              <strong style={{ fontWeight: 600, color: '#14181f' }}>{val}</strong>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 9px' }}>
            <span style={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Synch.</span>
            <strong style={{ fontWeight: 600, color: '#14181f', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{
                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                background: '#1f9d6b', boxShadow: '0 0 0 3px rgba(31,157,107,0.3)',
              }} />
              offline · 3
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main LandingPage component ────────────────────────────────────────────────

const landingJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: 'Pinveo',
      url: 'https://pinveo.app',
      logo: 'https://pinveo.app/icon-512.png',
    },
    {
      '@type': 'WebSite',
      name: 'Pinveo',
      url: 'https://pinveo.app',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Pinveo',
      applicationCategory: 'BusinessApplication',
      description:
        'Pinveo — aplikacja do dokumentacji terenowej: markery na rzutach PDF, formularze offline, raporty i eksporty dla firm inspekcyjnych i zarządców nieruchomości.',
      operatingSystem: 'Web, Android, iOS',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'PLN',
        description: 'Wczesny dostęp — testerzy korzystają bez opłat.',
      },
    },
  ],
};

export function LandingPage() {
  useScrollReveal();

  return (
    <MarketingLayout>
      <Seo
        title="Pinveo — dokumentacja terenowa"
        description="Markery na rzutach PDF, zdjęcia ze stemplem GPS i gotowe raporty — dla firm inspekcyjnych, zarządców nieruchomości i deweloperów. Pełna praca offline."
        path="/"
        jsonLd={landingJsonLd}
      />

      {/* ── HERO ── */}
      <main>
        <section
          className="mx-auto max-w-[1180px] px-7 bp-hero-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.02fr 1.1fr',
            gap: 54,
            alignItems: 'center',
            paddingTop: 64,
            paddingBottom: 40,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Left column */}
          <div>
            <p
              className="bp-rise bp-d1 font-['IBM_Plex_Mono'] text-[#c23f10] font-[600]"
              style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
            >
              ● Wczesny dostęp — dokumentacja terenowa
            </p>

            <h1
              className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
              style={{
                fontSize: 'clamp(38px, 5.2vw, 72px)',
                lineHeight: 1,
                letterSpacing: '-0.03em',
                marginTop: 20,
              }}
            >
              Raport gotowy,{' '}
              <span
                className="text-[#ff5a1f]"
                style={{ position: 'relative', whiteSpace: 'nowrap' }}
              >
                zanim zejdziesz
              </span>
              {' '}z&nbsp;obiektu.
            </h1>

            <p
              className="bp-rise bp-d3 text-[#3a414e]"
              style={{ fontSize: 18, maxWidth: '33em', marginTop: 22 }}
            >
              Wskazujesz punkt na rzucie PDF, robisz zdjęcie ze stemplem GPS i daty,
              wypełniasz formularz — wszystko bez zasięgu. Po powrocie nic nie zostaje
              do przepisania: raport i eksport generujesz jednym kliknięciem.
            </p>

            {/* CTA row */}
            <div className="bp-rise bp-d4 flex flex-wrap gap-3.5" style={{ marginTop: 28 }}>
              <Link
                to="/request-access"
                className="bp-btn bp-btn-hi font-['IBM_Plex_Mono'] font-[600] text-white"
                style={{
                  fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em',
                  padding: '12px 20px', border: '1.5px solid #ff5a1f',
                  background: '#ff5a1f', textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 9,
                }}
              >
                Poproś o wczesny dostęp <span aria-hidden="true">→</span>
              </Link>
              <Link
                to="/login"
                className="bp-btn font-['IBM_Plex_Mono'] font-[600] text-[#14181f]"
                style={{
                  fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em',
                  padding: '12px 20px', border: '1.5px solid #14181f',
                  background: 'transparent', textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center',
                }}
              >
                Przejdź do logowania
              </Link>
            </div>

            {/* Metrics grid */}
            <div className="bp-rise bp-d5 bp-metrics" style={{ marginTop: 36 }}>
              {metrics.map(([value, label]) => (
                <div key={value} className="bp-metric-cell">
                  <strong
                    className="block font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                    style={{ fontSize: 17 }}
                  >
                    {value}
                  </strong>
                  <span
                    className="block font-['IBM_Plex_Mono'] text-[#6b7280]"
                    style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — scena 3D; arkusz jest jej pierwszą klatką i wersją awaryjną */}
          <PlanScene
            className="lg:min-h-[520px]"
            scrollLinked
            fallback={<BlueprintSheet />}
            palette={LANDING_SCENE_PALETTE}
            labels={['M-18 · zawilgocenie', 'M-19 · pęknięcie', 'M-20 · instalacja']}
          />
        </section>

        {/* ── Ruler separator ── */}
        <div className="bp-ruler bp-ruler-thin" aria-hidden="true" />

        {/* ── FILARY (4) ── */}
        <section
          id="modules"
          className="relative z-[1]"
          style={{ padding: '84px 0' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div data-reveal style={{ maxWidth: '46rem' }}>
              <p
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                Dlaczego Pinveo
              </p>
              <h2
                className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 46px)',
                  lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                }}
              >
                Cztery filary dokumentacji terenowej.
              </h2>
              <p className="text-[#3a414e]" style={{ marginTop: 14, fontSize: 16 }}>
                Pinveo nie udaje ERP. Skupia krytyczny moment: zebranie danych w terenie
                i przekazanie ich dalej w uporządkowanej formie.
              </p>
            </div>

            <div
              className="bp-pillars-grid"
              data-reveal-group
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 18,
                marginTop: 40,
              }}
            >
              {modules.map((mod, i) => {
                const IconComp = pillarIcons[i] ?? IconFormBuilder;
                return (
                  <article
                    key={mod.title}
                    className="bp-pillar"
                    style={{
                      position: 'relative',
                      background: '#fbf8f1',
                      border: '1.5px solid #14181f',
                      padding: '26px 24px 24px',
                    }}
                  >
                    <span
                      className="font-['IBM_Plex_Mono'] text-[#6b7280]"
                      style={{
                        position: 'absolute', top: 14, right: 16,
                        fontSize: 12, letterSpacing: '0.1em',
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div
                      style={{
                        width: 48, height: 48,
                        border: '1.5px solid #14181f',
                        display: 'grid', placeItems: 'center',
                        background: '#f4efe6', color: '#14181f',
                      }}
                    >
                      <IconComp />
                    </div>
                    <h3
                      className="font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                      style={{ fontSize: 20, marginTop: 18 }}
                    >
                      {mod.title}
                    </h3>
                    <p className="text-[#3a414e]" style={{ fontSize: 14.5, marginTop: 9 }}>
                      {mod.description}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── MOBILE STRIP — aplikacja mobilna / offline ── */}
        <section
          className="relative z-[1]"
          style={{ padding: '60px 0', background: '#14181f' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div
              className="grid gap-8 lg:grid-cols-[300px_1fr]"
              style={{ alignItems: 'center' }}
            >
              <div className="bp-phone" data-reveal style={{ maxWidth: 300, width: '100%', margin: '0 auto' }}>
                <MobileMockup />
              </div>
              <div>
                <p
                  className="font-['IBM_Plex_Mono'] font-[600] text-[#ff5a1f]"
                  style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
                >
                  Aplikacja mobilna · iOS i Android
                </p>
                <h2
                  className="font-['Bricolage_Grotesque'] font-[800] text-[#f4efe6]"
                  style={{
                    fontSize: 'clamp(24px, 3vw, 38px)',
                    lineHeight: 1.04,
                    letterSpacing: '-0.02em',
                    marginTop: 10,
                  }}
                >
                  Pełna praca offline — piwnica, hala, budowa bez zasięgu.
                </h2>
                <p style={{ color: '#b9c0cc', fontSize: 15, marginTop: 14, lineHeight: 1.65, maxWidth: '38em' }}>
                  Markery, formularze i zdjęcia zapisują się lokalnie i synchronizują automatycznie
                  po powrocie zasięgu. Żaden wpis nie przepada — nawet bez sieci.
                </p>
                <div className="flex flex-wrap gap-2" style={{ marginTop: 18 }}>
                  {['Offline · tryb terenowy', 'Synchronizacja auto', 'Już teraz we wczesnym dostępie', 'Wkrótce w App Store i Google Play'].map((tag) => (
                    <span
                      key={tag}
                      className="font-['IBM_Plex_Mono'] font-[600]"
                      style={{
                        fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em',
                        padding: '3px 9px',
                        border: '1px solid rgba(244,239,230,0.25)',
                        color: 'rgba(244,239,230,0.7)',
                        background: 'transparent',
                      }}
                    >{tag}</span>
                  ))}
                </div>
                {/* Przeniesione z karty w hero: jedyna realna akcja dla aplikacji
                    mobilnej to wczesny dostęp (kafle sklepów były zapowiedzią). */}
                <p style={{ color: '#b9c0cc', fontSize: 14, marginTop: 18, lineHeight: 1.6, maxWidth: '38em' }}>
                  Uczestnicy wczesnego dostępu instalują aplikację już teraz — po zapisaniu się
                  wysyłamy zaproszenie z instrukcją.{' '}
                  <Link
                    to="/request-access"
                    className="font-['IBM_Plex_Mono'] font-[600] text-[#ff8a5c] underline decoration-[rgba(255,138,92,0.4)] underline-offset-4 transition-colors hover:text-[#f4efe6]"
                    style={{ fontSize: 13, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}
                  >
                    Chcę dostać aplikację <span aria-hidden="true">→</span>
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Ruler separator ── */}
        <div className="bp-ruler bp-ruler-thin" aria-hidden="true" />

        {/* ── PROCES (#workflow) ── */}
        <section
          id="workflow"
          className="relative z-[1]"
          style={{ padding: '84px 0', background: 'rgba(205,191,166,0.12)' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div data-reveal style={{ maxWidth: '46rem' }}>
              <p
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                Jak działa
              </p>
              <h2
                className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 46px)',
                  lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                }}
              >
                Od rzutu obiektu do&nbsp;gotowego raportu.
              </h2>
              <p className="text-[#3a414e]" style={{ marginTop: 14, fontSize: 16 }}>
                Cztery kroki które zamieniają inspekcję w terenie w gotową dokumentację.
              </p>
            </div>

            <ol
              className="bp-flow grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
              data-reveal-group
              style={{ marginTop: 40 }}
            >
              {workflow.map((item) => (
                <li
                  key={item.step}
                  style={{
                    position: 'relative',
                    background: '#fbf8f1',
                    border: '1.5px solid #cdbfa6',
                    padding: '22px 20px',
                  }}
                >
                  <span
                    className="bp-flow-num font-['IBM_Plex_Mono'] font-[600] text-[#ff5a1f]"
                    style={{ fontSize: 22, lineHeight: 1, letterSpacing: '-0.01em' }}
                  >
                    {item.step}
                  </span>
                  {/* ruled line under step number */}
                  <div style={{ height: 1, background: '#cdbfa6', margin: '12px 0' }} />
                  <h3
                    className="font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                    style={{ fontSize: 18 }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-[#3a414e]" style={{ fontSize: 13.5, marginTop: 9, lineHeight: 1.6 }}>
                    {item.description}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── ZAKRES APLIKACJI ── */}
        <section
          className="relative z-[1]"
          style={{ padding: '84px 0' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div
              className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]"
              style={{ alignItems: 'start' }}
            >
              <div>
                <p
                  className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                  style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
                >
                  Zakres aplikacji
                </p>
                <h2
                  className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                  style={{
                    fontSize: 'clamp(28px, 3.4vw, 42px)',
                    lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                  }}
                >
                  Więcej niż lista markerów na planie.
                </h2>
                <p className="text-[#3a414e]" style={{ marginTop: 14, fontSize: 16 }}>
                  Pinveo łączy kilka warstw pracy terenowej: lokalizację problemu na rzucie,
                  materiał zdjęciowy, formularz, status, historię zmian i raport końcowy.
                  Dzięki temu dokumentacja nie rozpada się na folder zdjęć, notatnik i arkusz Excel.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2" data-reveal-group>
                {featureGroups.map((group) => (
                  <article
                    key={group.title}
                    style={{
                      background: '#fbf8f1',
                      border: '1.5px solid #cdbfa6',
                      padding: '20px 18px',
                    }}
                  >
                    <h3
                      className="font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                      style={{ fontSize: 16 }}
                    >
                      {group.title}
                    </h3>
                    <ul style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {group.items.map((item) => (
                        <li key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <span
                            style={{
                              marginTop: 6, flexShrink: 0,
                              width: 6, height: 6,
                              borderRadius: '50%',
                              background: '#ff5a1f',
                            }}
                          />
                          <span className="text-[#3a414e]" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── TRUST BAND (dark) — Dane pod kontrolą ── */}
        <section
          className="bp-dark-band relative z-[1] overflow-hidden"
          style={{ background: '#14181f', padding: '84px 0' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div data-reveal style={{ maxWidth: '46rem' }}>
              <p
                className="font-['IBM_Plex_Mono'] font-[600] text-[#ff5a1f]"
                style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                Zaufanie i kontrola
              </p>
              <h2
                className="font-['Bricolage_Grotesque'] font-[800] text-[#f4efe6]"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 46px)',
                  lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                }}
              >
                Twoje dane zostają pod Twoją kontrolą.
              </h2>
              <p style={{ color: '#b9c0cc', marginTop: 14, fontSize: 16 }}>
                Pinveo nie zamyka Cię w swoim formacie. Dokumentacja z terenu jest Twoja —
                wyeksportujesz ją w każdej chwili, a praca offline chroni wpisy także przy braku zasięgu.
              </p>
            </div>

            <div
              className="bp-trust-grid"
              data-reveal-group
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                marginTop: 40,
                border: '1px solid rgba(255,255,255,0.13)',
              }}
            >
              {trustItems.map((item) => (
                <div key={item.tag} className="bp-trust-cell">
                  <p
                    className="font-['IBM_Plex_Mono'] font-[600] text-[#ff5a1f]"
                    style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}
                  >
                    {item.tag}
                  </p>
                  <h3
                    className="font-['Bricolage_Grotesque'] font-[700] text-[#f4efe6]"
                    style={{ fontSize: 17, margin: '10px 0 8px' }}
                  >
                    {item.title}
                  </h3>
                  <p style={{ fontSize: 13.5, color: '#b9c0cc', lineHeight: 1.55 }}>
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── DLA KOGO ── */}
        <section
          className="relative z-[1]"
          style={{ padding: '84px 0' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div data-reveal style={{ maxWidth: '46rem' }}>
              <p
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                Dla kogo
              </p>
              <h2
                className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 46px)',
                  lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                }}
              >
                Dla zespołów, które pracują na obiektach.
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-reveal-group style={{ marginTop: 40 }}>
              {forWhom.map((card) => (
                <article
                  key={card.title}
                  style={{
                    background: '#fbf8f1',
                    border: '1.5px solid #cdbfa6',
                    padding: '20px 18px',
                  }}
                >
                  <h3
                    className="font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                    style={{ fontSize: 17 }}
                  >
                    {card.title}
                  </h3>
                  <p className="text-[#3a414e]" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>
                    {card.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── USE-CASES ── */}
        <section
          id="use-cases"
          className="relative z-[1]"
          style={{ padding: '84px 0', background: 'rgba(205,191,166,0.12)' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div data-reveal style={{ maxWidth: '46rem' }}>
              <p
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                Zastosowania
              </p>
              <h2
                className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 46px)',
                  lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                }}
              >
                Dla zespołów, które dokumentują stan obiektów i&nbsp;oddają wynik klientowi.
              </h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2" data-reveal-group style={{ marginTop: 40 }}>
              {useCases.map((uc) => (
                <article
                  key={uc.title}
                  className="flex flex-col"
                  style={{
                    background: '#fbf8f1',
                    border: '1.5px solid #14181f',
                    padding: '24px 22px',
                  }}
                >
                  <h3
                    className="font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                    style={{ fontSize: 19 }}
                  >
                    {uc.title}
                  </h3>
                  <p className="text-[#3a414e] flex-1" style={{ fontSize: 14, marginTop: 10, lineHeight: 1.65 }}>
                    {uc.story}
                  </p>
                  <ul className="flex flex-wrap gap-2" style={{ marginTop: 16 }}>
                    {uc.tags.map((tag) => (
                      <li
                        key={tag}
                        className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                        style={{
                          fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em',
                          padding: '3px 8px', border: '1px solid #c23f10',
                          background: 'rgba(226,65,10,0.07)',
                        }}
                      >
                        {tag}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── ZASTOSOWANIA — linki do podstron /zastosowania/* ── */}
        <section
          id="zastosowania"
          className="relative z-[1]"
          style={{ padding: '84px 0' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div data-reveal style={{ maxWidth: '46rem' }}>
              <p
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                Zastosowania
              </p>
              <h2
                className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 46px)',
                  lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                }}
              >
                Dla jakiego rodzaju pracy?
              </h2>
              <p className="text-[#3a414e]" style={{ marginTop: 14, fontSize: 16 }}>
                Pinveo adaptujesz do swojej branży — kreator formularzy i markery na rzucie
                pasują do każdego, kto dokumentuje stan obiektów w terenie.
              </p>
            </div>

            <div
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
              data-reveal-group
              style={{ marginTop: 40 }}
            >
              {useCaseCards.map((uc) => (
                <Link
                  key={uc.slug}
                  to={`/zastosowania/${uc.slug}`}
                  className="bp-card-link"
                >
                  <article
                    className="bp-card-hover"
                    style={{
                      height: '100%',
                      background: '#fbf8f1',
                      border: '1.5px solid #cdbfa6',
                      padding: '20px 18px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <span
                      className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                      style={{
                        fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em',
                        padding: '2px 7px', border: '1px solid #c23f10',
                        background: 'rgba(226,65,10,0.07)', alignSelf: 'flex-start',
                      }}
                    >
                      {uc.label}
                    </span>
                    <h3
                      className="font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                      style={{ fontSize: 15, lineHeight: 1.3, marginTop: 2, flex: 1 }}
                    >
                      {uc.title}
                    </h3>
                    <span
                      className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                      style={{ fontSize: 12, letterSpacing: '0.04em' }}
                    >
                      Czytaj więcej <span className="bp-card-arrow" aria-hidden="true">→</span>
                    </span>
                  </article>
                </Link>
              ))}
            </div>

            <div style={{ marginTop: 28, textAlign: 'right' }}>
              <Link
                to="/zastosowania"
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 13, letterSpacing: '0.04em', textDecoration: 'none' }}
              >
                Wszystkie zastosowania →
              </Link>
            </div>
          </div>
        </section>

        {/* ── Ruler separator ── */}
        <div className="bp-ruler bp-ruler-thin" aria-hidden="true" />

        {/* ── Z BLOGA — linki do /blog/* ── */}
        <section
          id="blog"
          className="relative z-[1]"
          style={{ padding: '84px 0', background: 'rgba(205,191,166,0.12)' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div data-reveal style={{ maxWidth: '46rem' }}>
              <p
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                Z bloga / Baza wiedzy
              </p>
              <h2
                className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 46px)',
                  lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                }}
              >
                Artykuły o&nbsp;dokumentacji, odbiorach i&nbsp;przeglądach.
              </h2>
              <p className="text-[#3a414e]" style={{ marginTop: 14, fontSize: 16 }}>
                Praktyczna wiedza dla inspektorów, zarządców i firm budowlanych — terminy,
                przepisy i listy kontrolne do gotowego użycia.
              </p>
            </div>

            <div
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
              data-reveal-group
              style={{ marginTop: 40 }}
            >
              {blogCards.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="bp-card-link"
                >
                  <article
                    className="bp-card-hover"
                    style={{
                      height: '100%',
                      background: '#fbf8f1',
                      border: '1.5px solid #14181f',
                      padding: '20px 18px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <span
                      className="font-['IBM_Plex_Mono'] font-[600] text-[#6b7280]"
                      style={{
                        fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em',
                        padding: '2px 7px', border: '1px solid #cdbfa6',
                        background: 'transparent', alignSelf: 'flex-start',
                      }}
                    >
                      {post.label}
                    </span>
                    <h3
                      className="font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                      style={{ fontSize: 15, lineHeight: 1.35, marginTop: 2, flex: 1 }}
                    >
                      {post.title}
                    </h3>
                    <span
                      className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                      style={{ fontSize: 12, letterSpacing: '0.04em' }}
                    >
                      Czytaj artykuł <span className="bp-card-arrow" aria-hidden="true">→</span>
                    </span>
                  </article>
                </Link>
              ))}
            </div>

            <div style={{ marginTop: 28, textAlign: 'right' }}>
              <Link
                to="/blog"
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 13, letterSpacing: '0.04em', textDecoration: 'none' }}
              >
                Wszystkie artykuły →
              </Link>
            </div>
          </div>
        </section>

        {/* ── Ruler separator ── */}
        <div className="bp-ruler bp-ruler-thin" aria-hidden="true" />

        {/* ── FAQ ── */}
        <section
          id="faq"
          className="relative z-[1]"
          style={{ padding: '84px 0' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div data-reveal style={{ maxWidth: '46rem' }}>
              <p
                className="font-['IBM_Plex_Mono'] font-[600] text-[#c23f10]"
                style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                FAQ
              </p>
              <h2
                className="font-['Bricolage_Grotesque'] font-[800] text-[#14181f]"
                style={{
                  fontSize: 'clamp(30px, 3.6vw, 46px)',
                  lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 10,
                }}
              >
                Najczęstsze pytania.
              </h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2" data-reveal-group style={{ marginTop: 40 }}>
              {faqItems.map((item) => (
                <article
                  key={item.q}
                  style={{
                    background: '#fbf8f1',
                    border: '1.5px solid #cdbfa6',
                    padding: '20px 18px',
                  }}
                >
                  <h3
                    className="font-['Bricolage_Grotesque'] font-[700] text-[#14181f]"
                    style={{ fontSize: 16 }}
                  >
                    {item.q}
                  </h3>
                  <p className="text-[#3a414e]" style={{ fontSize: 14, marginTop: 9, lineHeight: 1.65 }}>
                    {item.a}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── EARLY ACCESS CTA ── */}
        <section
          id="access"
          className="relative z-[1]"
          style={{ padding: '84px 0 64px' }}
        >
          <div className="mx-auto max-w-[1180px] px-7">
            <div
              className="bp-cta-box"
              style={{
                background: '#14181f',
                border: '1.5px solid #14181f',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Blueprint grid overlay */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute', inset: 0, opacity: 0.11,
                  backgroundImage:
                    'linear-gradient(rgba(103,211,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(103,211,255,0.2) 1px, transparent 1px)',
                  backgroundSize: '30px 30px',
                  pointerEvents: 'none',
                }}
              />
              <div className="relative">
                <p
                  className="font-['IBM_Plex_Mono'] font-[600] text-[#ff5a1f]"
                  style={{ fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}
                >
                  Wczesny dostęp
                </p>
                <h2
                  className="font-['Bricolage_Grotesque'] font-[800] text-[#f4efe6]"
                  style={{
                    fontSize: 'clamp(28px, 3.8vw, 50px)',
                    lineHeight: 1.06, letterSpacing: '-0.02em', marginTop: 12,
                  }}
                >
                  Trwa wczesny dostęp — dołącz do&nbsp;testów Pinveo.
                </h2>
                <p style={{ color: 'rgba(244,239,230,0.82)', fontSize: 16, marginTop: 18, maxWidth: '42em', lineHeight: 1.65 }}>
                  Dostęp nie jest jeszcze publicznie otwarty. Opisz krótko swój przypadek użycia —
                  to pozwala dopasować konfigurację, szablony i zakres modułów do realnych potrzeb
                  Twojego zespołu.
                </p>
                <div className="flex flex-wrap gap-3.5" style={{ marginTop: 32 }}>
                  <Link
                    to="/request-access"
                    className="bp-btn font-['IBM_Plex_Mono'] font-[600]"
                    style={{
                      fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em',
                      padding: '13px 22px', border: '1.5px solid #f4efe6',
                      background: '#f4efe6', color: '#14181f', textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    Poproś o wczesny dostęp <span aria-hidden="true">→</span>
                  </Link>
                  <Link
                    to="/login"
                    className="bp-btn font-['IBM_Plex_Mono'] font-[600]"
                    style={{
                      fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em',
                      padding: '13px 22px', border: '1.5px solid rgba(244,239,230,0.35)',
                      background: 'transparent', color: '#f4efe6', textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center',
                    }}
                  >
                    Mam już konto
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </MarketingLayout>
  );
}
